import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { RevealOnScroll } from "@/components/site/Reveal";
import { CheckCircle2, Circle, Ship, ArrowRight } from "lucide-react";

function statusColour(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("delivered")) return "text-emerald-400 border-emerald-400/40";
  if (s.includes("cleared") || s.includes("ready")) return "text-[#C5A059] border-[#C5A059]/50";
  if (s.includes("arrived")) return "text-blue-300 border-blue-300/40";
  return "text-white/80 border-white/25";
}

export default function Track() {
  const [tracking, setTracking] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [shipment, setShipment] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setShipment(null);
    try {
      const { data } = await api.post("/track", { tracking_code: tracking, email });
      setShipment(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "We couldn't find a shipment matching those details.");
    } finally { setBusy(false); }
  };

  const reset = () => { setShipment(null); setTracking(""); setEmail(""); };

  return (
    <div data-testid="track-page" className="pt-40 pb-32">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-16 mb-16">
          <div className="lg:col-span-6">
            <div className="overline mb-6">Shipment Tracking</div>
            <RevealOnScroll
              element="h1"
              className="h-editorial text-[clamp(2.8rem,7vw,6rem)]"
              lines={["Follow your car,", "port to <em>door</em>."]}
            />
            <p className="mt-8 text-white/70 text-lg font-light leading-relaxed">
              Every Maklan Motors purchase is tracked from the manufacturer floor
              through the ocean to your driveway. Enter your tracking code and
              the email you provided at purchase to see live status.
            </p>
            <div className="mt-8 text-xs tracking-[0.22em] uppercase text-white/40">
              Demo credentials:<br/>
              <span className="text-white/70">MKM-2401-001 · kojo.demo@maklanmotors.com</span>
            </div>
          </div>

          <div className="lg:col-span-6">
            {!shipment ? (
              <form onSubmit={submit} data-testid="track-form" className="border border-white/10 p-8 md:p-10">
                <div className="flex items-start gap-4 mb-8">
                  <div className="w-11 h-11 border border-[#C5A059]/50 text-[#C5A059] flex items-center justify-center">
                    <Ship size={18} />
                  </div>
                  <div>
                    <div className="font-display text-2xl">Track a shipment</div>
                    <div className="text-white/50 text-sm mt-1">Enter your details below.</div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <label className="field-label">Tracking Code</label>
                    <input
                      required
                      value={tracking}
                      onChange={(e) => setTracking(e.target.value.toUpperCase())}
                      placeholder="e.g. MKM-2401-001"
                      className="field"
                      data-testid="track-code-input"
                    />
                  </div>
                  <div>
                    <label className="field-label">Email on File</label>
                    <input
                      required type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="field"
                      data-testid="track-email-input"
                    />
                  </div>
                </div>
                {error && <div className="mt-6 text-sm text-red-300 border border-red-500/30 bg-red-500/5 p-4" data-testid="track-error">{error}</div>}
                <button disabled={busy} className="btn-gold mt-8 w-full" data-testid="track-submit">
                  {busy ? "Tracking…" : (<>Track Shipment <ArrowRight className="ml-2" size={14} /></>)}
                </button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                data-testid="track-summary"
                className="border border-[#C5A059]/40 bg-gradient-to-b from-[#C5A059]/[0.06] to-transparent p-8 md:p-10"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="overline">{shipment.tracking_code}</div>
                    <div className="font-display text-2xl mt-2">{shipment.vehicle}</div>
                    <div className="text-white/50 text-sm mt-1">
                      {shipment.origin_port} → {shipment.destination_port}
                    </div>
                  </div>
                  <span className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border ${statusColour(shipment.status)}`}>
                    {shipment.status}
                  </span>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-4 border-t border-white/10 pt-6">
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">Customer</div>
                    <div className="mt-1">{shipment.customer_name}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">ETA</div>
                    <div className="mt-1">{shipment.eta || "TBC"}</div>
                  </div>
                </div>
                {shipment.notes && (
                  <div className="mt-6 text-sm text-white/60 italic">"{shipment.notes}"</div>
                )}
                <button onClick={reset} className="mt-6 text-xs tracking-[0.2em] uppercase text-[#C5A059] link-under" data-testid="track-reset">
                  Track another shipment
                </button>
              </motion.div>
            )}
          </div>
        </div>

        {/* Timeline */}
        {shipment && (
          <div className="border-t border-white/10 pt-16" data-testid="track-timeline">
            <div className="overline mb-6">Milestones</div>
            <ol className="relative border-l border-white/10 ml-3 space-y-10">
              {shipment.milestones.map((m, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="pl-10 relative"
                  data-testid={`milestone-${i}`}
                >
                  <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-[#050814] flex items-center justify-center">
                    {m.completed
                      ? <CheckCircle2 size={20} className="text-[#C5A059]" />
                      : <Circle size={20} className="text-white/30" />}
                  </span>
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div className={`font-display text-xl ${m.completed ? "text-white" : "text-white/50"}`}>{m.name}</div>
                    {m.date && <div className="text-xs tracking-[0.2em] uppercase text-white/40">{m.date}</div>}
                  </div>
                  {m.note && <div className="mt-1 text-sm text-white/60 max-w-2xl">{m.note}</div>}
                </motion.li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
